import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2, MapPin, Minus, Snowflake } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { LatLngExpression } from 'leaflet'
import { useAccounts } from '@/lib/use-accounts'
import {
  useSearchFilters,
  useSnowHuntStatus,
  useStartSnowHunt,
} from '@/lib/api'
import { useFavoriteLocations } from '@/lib/api/queries/favorite-locations'
import { useFeatureFlag } from '@/lib/use-feature-flags'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { ErrorBoundary } from '@/components/error-boundary'
import { MapSection, RadiusControl } from '@/components/search'
import { localizeDigits } from '@/lib/localize-digits'

const MONTREAL_CENTER: LatLngExpression = [45.5017, -73.5673]

interface HuntingAccountConfig {
  account_name: string
  center: LatLngExpression
  radius: number // in meters
}

export const Route = createFileRoute('/snow-hunt')({
  component: SnowHuntPage,
})

function SnowHuntPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { accounts: accountsList, isLoading: accountsLoading } = useAccounts()
  const startSnowHuntMutation = useStartSnowHunt()

  // Check if there's an active hunt for any account
  const firstAccountId =
    accountsList.length > 0 ? accountsList[0].id : undefined
  const { data: activeHuntStatus } = useSnowHuntStatus(
    firstAccountId,
    !accountsLoading && !!firstAccountId,
  )
  const isHuntActive =
    activeHuntStatus?.status === 'searching' ||
    activeHuntStatus?.status === 'optimizing' ||
    activeHuntStatus?.status === 'booked'

  const [favoriteLocationsEnabled] = useFeatureFlag('analytics')
  const { data: favoriteLocationsData } = useFavoriteLocations(
    favoriteLocationsEnabled,
  )

  // Hunting accounts with per-account search config
  const [huntingAccounts, setHuntingAccounts] = useState<
    Array<HuntingAccountConfig>
  >([])

  // Active hunting account being configured (index)
  const [activeAccountIdx, setActiveAccountIdx] = useState(0)

  // Last-use check settings
  const [lastUseCheck, setLastUseCheck] = useState<
    'skip' | 'check_before_booking' | 'book_first_check_after'
  >('skip')
  const [lastUseThresholdHours, setLastUseThresholdHours] = useState(24)

  // Post-booking optimization
  const [enableOptimization, setEnableOptimization] = useState(true)

  // Filters
  const [filters, setFilters] = useState<Record<string, boolean>>({})
  const [showFilters, setShowFilters] = useState(false)
  const { data: availableFilters, isLoading: filtersLoading } =
    useSearchFilters()

  // User location
  const [userLocation, setUserLocation] = useState<LatLngExpression | null>(
    null,
  )

  // Manual radius input state for active secondary account
  const [showManualInput, setShowManualInput] = useState(false)
  const [manualRadiusValue, setManualRadiusValue] = useState('300')

  // Initialize filters with defaults
  useEffect(() => {
    if (availableFilters?.filters && Object.keys(filters).length === 0) {
      const defaultFilters: Record<string, boolean> = {}
      availableFilters.filters.forEach((filter) => {
        defaultFilters[filter.name] = filter.default
      })
      setFilters(defaultFilters)
    }
  }, [availableFilters, filters])

  // Get user location on mount
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setUserLocation([latitude, longitude])
      },
      () => {
        // Silently fall back to Montreal
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    )
  }, [])

  // Auto-initialize all accounts as hunting accounts
  useEffect(() => {
    if (accountsList.length > 0 && huntingAccounts.length === 0) {
      const allAccounts = accountsList.map((acc) => ({
        account_name: acc.id,
        center: userLocation || MONTREAL_CENTER,
        radius: 300,
      }))
      setHuntingAccounts(allAccounts)
    }
  }, [accountsList, huntingAccounts.length, userLocation])

  const activeAccount = huntingAccounts[activeAccountIdx]

  const handleCenterChange = (center: LatLngExpression) => {
    setHuntingAccounts((prev) =>
      prev.map((acc, i) =>
        i === activeAccountIdx ? { ...acc, center } : acc,
      ),
    )
  }

  const handleRadiusChange = (radius: number) => {
    setHuntingAccounts((prev) =>
      prev.map((acc, i) =>
        i === activeAccountIdx ? { ...acc, radius } : acc,
      ),
    )
  }

  const handleGoToUserLocation = () => {
    if (userLocation) {
      handleCenterChange(userLocation)
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const location: LatLngExpression = [latitude, longitude]
          setUserLocation(location)
          handleCenterChange(location)
        },
        () => {
          toast.error(t('search.unableToGetLocation'))
        },
      )
    }
  }

  const handleToggleAccount = (accountId: string) => {
    const isSelected = huntingAccounts.some(
      (a) => a.account_name === accountId,
    )
    if (isSelected) {
      if (huntingAccounts.length <= 1) {
        toast.warning(t('snowHunt.atLeastOneAccount'))
        return
      }
      const removedIdx = huntingAccounts.findIndex(
        (a) => a.account_name === accountId,
      )
      setHuntingAccounts((prev) =>
        prev.filter((a) => a.account_name !== accountId),
      )
      if (activeAccountIdx >= huntingAccounts.length - 1) {
        setActiveAccountIdx(Math.max(0, huntingAccounts.length - 2))
      } else if (removedIdx < activeAccountIdx) {
        setActiveAccountIdx((prev) => prev - 1)
      }
    } else {
      setHuntingAccounts((prev) => [
        ...prev,
        {
          account_name: accountId,
          center: userLocation || MONTREAL_CENTER,
          radius: 300,
        },
      ])
    }
  }

  const handleStartHunt = async () => {
    if (huntingAccounts.length === 0) {
      toast.warning(t('snowHunt.atLeastOneAccount'))
      return
    }

    try {
      await startSnowHuntMutation.mutateAsync({
        params: {
          primary_account: accountsList[0].id,
          secondary_accounts: huntingAccounts.map((acc) => {
            const centerArray = Array.isArray(acc.center)
              ? acc.center
              : [acc.center.lat, acc.center.lng]
            return {
              account_name: acc.account_name,
              point: {
                latitude: centerArray[0],
                longitude: centerArray[1],
              },
              radius: acc.radius / 1000, // meters to km
            }
          }),
          last_use_check: lastUseCheck,
          last_use_threshold_hours: lastUseThresholdHours,
          enable_post_booking_optimization: enableOptimization,
          additional_filters: filters,
        },
      })

      navigate({ to: '/snow-hunt/status' })
    } catch (error) {
      console.error('Failed to start snow hunt:', error)
      toast.error(t('snowHunt.startFailed'))
    }
  }

  return (
    <div className="fixed-fullscreen-safe fixed flex flex-col">
      {/* Map for active secondary account */}
      <ErrorBoundary
        fallback={
          <div className="flex-1 flex items-center justify-center bg-muted">
            <p className="text-muted-foreground">Failed to load map</p>
          </div>
        }
      >
        {huntingAccounts.length > 0 ? (
          <MapSection
            searchCenter={activeAccount.center}
            searchRadius={activeAccount.radius}
            onCenterChange={handleCenterChange}
            onGoToUserLocation={handleGoToUserLocation}
            onToggleFilters={() => setShowFilters(!showFilters)}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-muted">
            <p className="text-muted-foreground">
              {t('snowHunt.selectAccounts')}
            </p>
          </div>
        )}
      </ErrorBoundary>

      {/* Bottom Configuration Panel */}
      <div className="bg-background/95 backdrop-blur border-t border-border max-h-[55vh] overflow-y-auto">
        <div className="container mx-auto max-w-screen-md px-4 py-3 space-y-3">
          {/* Active Hunt Banner */}
          {isHuntActive && (
            <Link to="/snow-hunt/status">
              <Card className="border-blue-500/50 bg-blue-500/10 cursor-pointer hover:bg-blue-500/20 transition-colors">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    <span className="text-sm font-medium">
                      {t('snowHunt.huntActive')}
                    </span>
                    <Badge variant="default" className="text-xs">
                      {activeHuntStatus.status}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {t('snowHunt.viewStatus')} &rarr;
                  </span>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Favorite Location Quick Access */}
          {favoriteLocationsEnabled &&
            favoriteLocationsData &&
            favoriteLocationsData.locations.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {favoriteLocationsData.locations.map((loc) => (
                  <Button
                    key={loc.id}
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5"
                    onClick={() => {
                      handleCenterChange([loc.latitude, loc.longitude])
                      handleRadiusChange(loc.radius * 1000)
                      setManualRadiusValue(String(loc.radius * 1000))
                    }}
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    {loc.name}
                  </Button>
                ))}
              </div>
            )}

          {/* Hunting Accounts */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">
              {t('snowHunt.huntingAccounts')}
            </Label>
            <p className="text-xs text-muted-foreground mb-1">
              {t('snowHunt.selectAccounts')}
            </p>
            <div className="flex gap-2 overflow-x-auto">
              {accountsList.map((acc) => {
                const isSelected = huntingAccounts.some(
                  (a) => a.account_name === acc.id,
                )
                return (
                  <Button
                    key={acc.id}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    className="shrink-0"
                    onClick={() => handleToggleAccount(acc.id)}
                    disabled={isSelected && huntingAccounts.length <= 1}
                  >
                    {acc.name}
                  </Button>
                )
              })}
            </div>
          </div>

          {/* Account Area Tabs */}
          {huntingAccounts.length > 1 && (
            <div className="flex gap-1 overflow-x-auto">
              {huntingAccounts.map((acc, idx) => (
                <Button
                  key={acc.account_name}
                  variant={activeAccountIdx === idx ? 'default' : 'outline'}
                  size="sm"
                  className="shrink-0"
                  onClick={() => setActiveAccountIdx(idx)}
                >
                  {acc.account_name}
                </Button>
              ))}
            </div>
          )}

          {/* Radius Control for Active Account */}
          {huntingAccounts.length > 0 && (
            <RadiusControl
              searchRadius={activeAccount.radius}
              showManualInput={showManualInput}
              manualRadiusValue={manualRadiusValue}
              onRadiusChange={handleRadiusChange}
              onManualValueChange={setManualRadiusValue}
              onShowManualInput={setShowManualInput}
            />
          )}

          {/* Last-Use Check Mode */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">
              {t('snowHunt.lastUseCheck')}
            </Label>
            <div className="flex gap-1 mt-1 flex-wrap">
              {(
                [
                  'skip',
                  'check_before_booking',
                  'book_first_check_after',
                ] as const
              ).map((mode) => (
                <Button
                  key={mode}
                  variant={lastUseCheck === mode ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs"
                  onClick={() => setLastUseCheck(mode)}
                >
                  {t(`snowHunt.lastUseMode.${mode}`)}
                </Button>
              ))}
            </div>
            {lastUseCheck !== 'skip' && (
              <div className="flex items-center gap-2 mt-2">
                <Label className="text-xs">
                  {t('snowHunt.thresholdHours')}
                </Label>
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={lastUseThresholdHours}
                  onChange={(e) =>
                    setLastUseThresholdHours(Number(e.target.value))
                  }
                  className="w-16 px-2 py-1 text-sm border border-border rounded bg-background text-right"
                />
                <span className="text-xs text-muted-foreground">
                  {t('snowHunt.hours')}
                </span>
              </div>
            )}
          </div>

          {/* Post-Booking Optimization Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">
                {t('snowHunt.postBookingOptimization')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('snowHunt.postBookingOptimizationDesc')}
              </p>
            </div>
            <Switch
              checked={enableOptimization}
              onCheckedChange={setEnableOptimization}
            />
          </div>

          {/* Snow Car Always On Badge */}
          <div className="flex items-center gap-2">
            <Badge variant="default" className="bg-blue-600">
              <Snowflake className="h-3 w-3 me-1" />
              {t('snowHunt.snowCarAlwaysOn')}
            </Badge>
          </div>

          {/* Start Button */}
          <Button
            className="w-full"
            size="lg"
            onClick={handleStartHunt}
            disabled={
              startSnowHuntMutation.isPending ||
              huntingAccounts.length === 0 ||
              accountsLoading
            }
          >
            <Snowflake className="h-5 w-5 me-2" />
            {startSnowHuntMutation.isPending
              ? t('common.loading') + '...'
              : t('snowHunt.startHunt')}
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="absolute inset-0 z-[1001] bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-x-0 bottom-0 z-[1002] max-h-[60vh] flex flex-col rounded-t-2xl bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border p-4 shrink-0">
              <span className="text-base font-semibold">
                {t('search.filtersTitle')}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowFilters(false)}
              >
                <Minus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {filtersLoading && (
                <div className="text-center text-muted-foreground py-4">
                  {t('common.loading')}...
                </div>
              )}
              {!filtersLoading &&
                availableFilters?.filters.map((filter) => (
                  <div
                    key={filter.name}
                    className="flex items-center justify-between"
                  >
                    <div className="flex-1 pe-4">
                      <label className="text-sm font-medium">
                        {filter.name
                          .split('_')
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(' ')}
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {filter.description}
                      </p>
                    </div>
                    <Switch
                      checked={filters[filter.name] ?? filter.default}
                      onCheckedChange={(checked) =>
                        setFilters({ ...filters, [filter.name]: checked })
                      }
                      disabled={filter.name === 'snow_car'}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
